import { computed, ref, watch } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { createCart, getCart } from '@/api/endpoints/cartEndpoints.ts';
import { useInterval, useLocalStorage } from '@vueuse/core';
import { isAxiosError } from 'axios';
import { defineStore } from 'pinia';

export const CARD_UUID_STORAGE_KEY = 'cartUuid';

export const useCartStore = defineStore('cart', () => {
  const hasLoadingThumbnail = ref(false);

  const cartUuid = useLocalStorage<string | null>(CARD_UUID_STORAGE_KEY, null);
  const createCartMutation = useMutation({
    mutationFn: createCart,
    onSuccess: (cart) => {
      cartUuid.value = cart.uuid;
    },
  });

  watch(
    cartUuid,
    () => {
      if (!cartUuid.value) {
        createCartMutation.mutate();
      }
    },
    {
      immediate: true,
    },
  );

  const createNewCart = async () => {
    const cart = await createCartMutation.mutateAsync();
    cartUuid.value = cart.uuid;
  };

  const {
    data: cart,
    error,
    isError,
    ...query
  } = useQuery({
    queryFn: () => getCart(cartUuid.value!),
    queryKey: computed(() => ['cart', cartUuid.value]),
    enabled: computed(() => !!cartUuid.value),
    retry: 1,
  });

  const { pause, resume } = useInterval(2000, {
    controls: true,
    callback: () => {
      if (!query.isFetching.value) {
        query.refetch();
      }
    },
  });

  watch(error, () => {
    if (error.value) {
      if (isAxiosError(error.value) && error.value.response?.status === 404) {
        createNewCart();
      }
    }
  });

  watch(
    [cart, isError],
    ([cart, isError]) => {
      if (cart && !isError) {
        for (const item of cart.items) {
          if (item.hasThumbnail === null) {
            resume();
            return;
          }
        }
      }
      pause();
    },
    {
      immediate: true,
    },
  );

  const cartTotal = computed(() => {
    if (!cart.value) {
      return 0;
    }

    return cart.value.items.reduce(
      (acc, item) => acc + item.quantity * item.ppuInclVat,
      0,
    );
  });

  return {
    cartUuid,
    cart,
    cartTotal,
    error,
    ...query,
    hasLoadingThumbnail,
    createNewCart,
  };
});
